/**
 * Constantes y helpers compartidos del módulo de Campañas (listado + editor).
 * Única fuente de verdad para canales, categorías, estados e i18n de contenido.
 */

export const LANGS = ["es", "en", "fr", "pt", "ru"];

export const CHANNELS = {
  push: {
    value: "push",
    label: "Push",
    icon: "notification",
    color: "#B1E5FC",
    description: "Notificación al móvil. Directa y efímera.",
  },
  mail: {
    value: "mail",
    label: "Email",
    icon: "mail",
    color: "#FFBC99",
    description: "Correo con la plantilla corporativa.",
  },
  inapp: {
    value: "inapp",
    label: "In-App",
    icon: "message",
    color: "#CABDFF",
    description: "Mensaje en el buzón dentro de la app.",
  },
};

export const CATEGORIES = [
  {
    value: "marketing",
    label: "Marketing",
    hint: "Respeta el opt-in (newsletter / push marketing).",
  },
  {
    value: "announcement",
    label: "Anuncio",
    hint: "Novedades de producto. Respeta el opt-in.",
  },
  {
    value: "transactional",
    label: "Transaccional",
    hint: "Responde a una acción del usuario. Ignora el opt-out.",
  },
];

// Estado → etiqueta + clase global del template (status-*).
export const STATUS = {
  draft: { label: "Borrador", className: "status-yellow" },
  scheduled: { label: "Programada", className: "status-blue" },
  sending: { label: "Enviando", className: "status-purple" },
  paused: { label: "En pausa", className: "status-yellow" },
  sent: { label: "Enviada", className: "status-green" }, // histórico legacy
  completed: { label: "Enviada", className: "status-green" },
  failed: { label: "Fallida", className: "status-red" },
  cancelled: { label: "Cancelada", className: "status-red-dark" },
};

export const emptyLangMap = () => LANGS.reduce((acc, l) => ({ ...acc, [l]: "" }), {});

/** {es:"…"} → Translation[] ({active, lang, trans}) para el backend. */
export const toTranslations = (map) =>
  LANGS.filter((l) => map[l] && String(map[l]).trim() !== "").map((l) => ({
    active: true,
    lang: l,
    trans: map[l],
  }));

/** Translation[] → {es:"…"} para el formulario. */
export const fromTranslations = (translations) => {
  const map = emptyLangMap();
  (translations || []).forEach((t) => {
    if (t?.lang && map[t.lang] !== undefined) map[t.lang] = t.trans || "";
  });
  return map;
};

/** Resuelve una Translation[] a un idioma con fallback (mismo criterio que el backend). */
export const resolveTrans = (translations, lang, fallback = "en") => {
  if (!Array.isArray(translations) || !translations.length) return "";
  const pick = (l) => translations.find((t) => t?.lang === l && t?.trans)?.trans;
  return pick(lang) || pick(fallback) || translations.find((t) => t?.trans)?.trans || "";
};

/** Resumen humano de una SegmentDefinition para el listado y confirmaciones. */
/** Canales efectivos (multi-canal con compat del campo singular). */
export const channelsOf = (c) =>
  c?.channels?.length ? c.channels : c?.channel ? [c.channel] : [];

export const segmentSummary = (segment) => {
  if (!segment || Object.keys(segment).length === 0) return "Todos los usuarios";
  const parts = [];
  if (segment.userIds?.length) parts.push(`${segment.userIds.length} usuario(s) concretos`);
  if (segment.inactiveForDays) parts.push(`inactivos ${segment.inactiveForDays} d`);
  if (segment.neverPlayed) parts.push("nunca jugaron");
  if (segment.registeredInLastDays) parts.push(`alta ≤ ${segment.registeredInLastDays} d`);
  if (segment.premium === true) parts.push("premium");
  if (segment.premium === false) parts.push("no premium");
  if (segment.levels?.length) parts.push(segment.levels.join("/"));
  if (segment.langs?.length) parts.push(segment.langs.map((l) => l.toUpperCase()).join("·"));
  if (segment.newsletterOnly) parts.push("newsletter");
  if (segment.confirmedMailOnly) parts.push("mail confirmado");
  if (segment.minNumPlayed) parts.push(`≥ ${segment.minNumPlayed} partidas`);
  if (segment.maxNumPlayed != null) parts.push(`≤ ${segment.maxNumPlayed} partidas`);
  if (segment.excludeCampaignIds?.length)
    parts.push(`sin repetir ${segment.excludeCampaignIds.length} campaña(s)`);
  if (segment.limit) parts.push(`máx ${segment.limit}`);
  return parts.length ? parts.join(" · ") : "Todos los usuarios";
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
};

export const fmtDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-ES", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** ¿La campaña aún puede editarse/borrarse? Excluye sending: ya está en curso. */
export const isEditable = (c) =>
  !c?.legacy && ["draft", "scheduled", "cancelled"].includes(c?.status);

/**
 * ¿Se puede (re)enviar? Incluye TODO sending (con o sin progreso): el ledger es
 * idempotente por (campaign, user, channel), así que reintentar es siempre seguro
 * — a los ya entregados el processor los salta. Sin este botón el admin no tiene
 * salida cuando una campaña se queda a medias.
 */
export const isDispatchable = (c) => {
  if (c?.legacy) return false;
  return ["draft", "scheduled", "cancelled", "sending", "paused"].includes(c?.status);
};

/** Estado a mostrar: SENDING sin progreso se pinta como "En cola" (no engaña). */
export const statusOf = (c) => {
  const key = c?.status || "draft";
  if (key === "sending") {
    const s = c?.stats || {};
    const processed = (s.sent || 0) + (s.failed || 0) + (s.skipped || 0);
    if (processed === 0) return { label: "En cola", className: "status-yellow" };
  }
  return STATUS[key] || { label: key, className: "status-yellow" };
};

/** Sanea HTML pegado/generado del cuerpo de mail (solo formato básico). */
export const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || "";
};
